import React from 'react';
import { formatCurrency } from '../../utils/commonUtils';
import { StatisticsTable } from './StatisticsTable';
import { StatisticsChart } from './StatisticsChart';

interface TopProduct {
  product_id: number;
  product_name: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  average_quantity_per_order: number;
}

interface ProductSalesByCategory {
  category_name: string;
  total_quantity: number;
  total_revenue: number;
  product_count: number;
  average_quantity_per_product: number;
}

interface ProductSalesTrend {
  date: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
  unique_products: number;
}

interface MostPopularProduct {
  product_id: number;
  product_name: string;
  unique_users: number;
  total_quantity: number;
  total_revenue: number;
  average_quantity_per_user: number;
}

interface ProductSalesByDepartment {
  management: string;
  total_quantity: number;
  total_revenue: number;
  unique_users: number;
  average_quantity_per_user: number;
}

interface ProductSalesStatisticsSectionProps {
  topProductsByQuantity: TopProduct[];
  topProductsByRevenue: TopProduct[];
  productSalesByCategory: ProductSalesByCategory[];
  productSalesTrends: ProductSalesTrend[];
  mostPopularProducts: MostPopularProduct[];
  productSalesByDepartment: ProductSalesByDepartment[];
}

const ProductSalesStatisticsSection: React.FC<ProductSalesStatisticsSectionProps> = ({
  topProductsByQuantity,
  topProductsByRevenue,
  productSalesByCategory,
  productSalesTrends,
  mostPopularProducts,
  productSalesByDepartment,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
    {/* En Çok Satılan Ürünler */}
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">En Çok Satılan Ürünler</h3>
      <div className="flex-1 overflow-auto">
        <StatisticsTable
          columns={[
            { key: 'product_name', label: 'Ürün' },
            { key: 'total_quantity', label: 'Toplam Satış' },
            { key: 'order_count', label: 'Sipariş Sayısı' },
            { key: 'total_revenue', label: 'Toplam Gelir', render: row => formatCurrency(row.total_revenue) },
          ]}
          data={topProductsByQuantity}
        />
      </div>
    </div>
    
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">En Çok Satılan Ürünler (Bar Chart)</h3>
      <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
        <StatisticsChart
          type="bar"
          data={topProductsByQuantity}
          dataKey="total_quantity"
          xKey="product_name"
          title=""
          color="#a21caf"
          height={260}
        />
      </div>
    </div>
    
    {/* En Çok Gelir Getiren Ürünler */}
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">En Çok Gelir Getiren Ürünler</h3>
      <div className="flex-1 overflow-auto">
        <StatisticsTable
          columns={[
            { key: 'product_name', label: 'Ürün' },
            { key: 'total_revenue', label: 'Toplam Gelir', render: row => formatCurrency(row.total_revenue) },
            { key: 'total_quantity', label: 'Toplam Satış' },
            { key: 'order_count', label: 'Sipariş Sayısı' },
          ]}
          data={topProductsByRevenue}
        />
      </div>
    </div>
    
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">En Çok Gelir Getiren Ürünler (Bar Chart)</h3>
      <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
        <StatisticsChart
          type="bar"
          data={topProductsByRevenue}
          dataKey="total_revenue"
          xKey="product_name"
          title=""
          color="#f59e42"
          height={260}
        />
      </div>
    </div>
    
    {/* Kategori Bazında Satışlar */}
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">Kategori Bazında Satışlar</h3>
      <div className="flex-1 overflow-auto">
        <StatisticsTable
          columns={[
            { key: 'category_name', label: 'Kategori' },
            { key: 'total_quantity', label: 'Toplam Satış' },
            { key: 'total_revenue', label: 'Toplam Gelir', render: row => formatCurrency(row.total_revenue) },
            { key: 'product_count', label: 'Ürün Sayısı' },
          ]}
          data={productSalesByCategory}
        />
      </div>
    </div>
    
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">Kategoriye Göre Satış Dağılımı</h3>
      <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
        <StatisticsChart
          type="pie"
          data={productSalesByCategory}
          dataKey="total_quantity"
          nameKey="category_name"
          title=""
          height={260}
        />
      </div>
    </div>
    
    {/* Satış Trendleri */}
    <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col md:col-span-2 h-[370px]">
      <h3 className="font-bold text-lg mb-4 text-gray-800">Zamana Göre Satış Trendleri</h3>
      <div className="flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden">
        <StatisticsChart
          type="line"
          data={productSalesTrends}
          dataKey="total_quantity"
          xKey="date"
          lineKey="total_quantity"
          title=""
          color="#3b82f6"
          height={260}
        />
      </div>
    </div>
  </div>
);

export default ProductSalesStatisticsSection; 